<template>
  <div class="d-flex flex-column gap-1 cod-age-filter">
    <div
      v-for="[key, condition] in conditions"
      :key="`cod-cond-${key}`"
      class="d-flex gap-1 align-items-center"
    >
      <select
        v-model="condition.op"
        class="form-select form-select-sm cod-age-op"
        @change="$emit('filter')"
      >
        <option
          v-for="op in OPERATOR_OPTIONS"
          :key="`${key}-op-${op.value}`"
          :value="op.value"
        >
          {{ op.label }}
        </option>
      </select>
      <select
        v-model="condition.value"
        class="form-select form-select-sm flex-grow-1"
        @change="$emit('filter')"
      >
        <option :value="null">
          {{ $t("dashboard.portfolio.table.filters.cod_age_year_placeholder") }}
        </option>
        <option
          v-for="year in yearOptions"
          :key="`${key}-year-${year.value}`"
          :value="year.value"
        >
          {{ year.label }}
        </option>
      </select>
      <button
        v-if="key === 'b'"
        type="button"
        class="btn btn-link btn-sm p-0 flex-shrink-0"
        :aria-label="$t('common.remove')"
        @click="removeSecondCondition"
      >
        <font-awesome-icon icon="xmark" />
      </button>
    </div>

    <button
      v-if="!model.b"
      type="button"
      class="btn btn-link btn-sm p-0 text-start"
      @click="addSecondCondition"
    >
      {{ $t("dashboard.portfolio.table.filters.cod_age_add_condition") }}
    </button>
  </div>
</template>

<script setup lang="ts">
import {
  COD_AGE_OPERATORS,
  type CodAgeCond,
  type CodAgeFilterValue,
} from "@/dashboard/inventory_management/common/portfolioTableColumns";
import { computed } from "vue";

const OPERATOR_OPTIONS = Object.entries(COD_AGE_OPERATORS).map(
  ([value, label]) => ({ label, value })
);

/**
 * The COD age column's filter: one or two (operator, year) conditions. Bind the slot's
 * `filterModel.value` with `v-model` and call its `filterCallback()` on `filter`. The editor
 * changes that object in place rather than replacing it, because
 * the DataTable reads the edited filter back from its own copy of the filter model.
 */
defineProps<{
  yearOptions: { label: string; value: number }[];
}>();

const emit = defineEmits<{
  filter: [];
}>();

const model = defineModel<CodAgeFilterValue>({ required: true });

const conditions = computed((): ["a" | "b", CodAgeCond][] =>
  model.value.b
    ? [
        ["a", model.value.a],
        ["b", model.value.b],
      ]
    : [["a", model.value.a]]
);

// A condition without a year filters nothing, but the table hands out fresh filter models whenever
// its filters change, so it has to hear of the new row to keep it.
const addSecondCondition = () => {
  model.value.b = { op: "LT", value: null };
  emit("filter");
};

const removeSecondCondition = () => {
  model.value.b = null;
  emit("filter");
};
</script>

<style scoped lang="scss">
.cod-age-op {
  flex: 0 0 3.25rem;
  padding-right: 1.25rem;
  background-position: right 0.3rem center;
  text-align: center;
}
</style>
